import {
  ActionSubjectFilter,
  ActionSubjectOption,
  ActionsAndSubjectsResponse,
  FilterDependsOn,
  OptionDependsOn
} from '@/types/WorkflowCreate/AppConfiguration'
import {
  getActionsAndSubjectsByApp,
  getAppAvailableFields,
  getLoginMethodsOfApp
} from '@/services/applicationsService'
import errorIcon from '@/assets/icons/error.svg'
import { SquarePen, Trash2 } from "lucide-react"
import { AppDispatch, RootState } from "@/redux"
import { useDispatch, useSelector } from "react-redux"
import { Section } from "../Section"
import { InputFieldWrapper } from "../InputFieldsSection"
import { AppName } from "../AppName"
import { EditTaskApp } from "../Modals/EditTaskApp"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { Button } from "@/components/ui/button"
import { useEffect, useRef, useState } from "react"
import { getAllActionsByApp } from '@/pages/Workflows/helpers/actions'
import { getAllSubjectsByAppAndAction } from '@/pages/Workflows/helpers/subjects'
import { DestinationSelectInputField } from '@/components/custom/InputField/WorkflowSelectInput'
import { CreateAndUseNewCredential } from '@/pages/Workflows/components/Modals/UseNewCredentials'
import { LoginMethod } from '@/types/credentials'
import { ButtonVariant } from '@/constants/button/button'
import { AppFieldsToFetch, FieldsResponse } from '@/types/WorkflowCreate/fields'
import { StepFilters } from '../Filters/StepFilter'
import { WFCreateStep } from '@/types/WorkflowCreate/workflowCreate'
import { StepOptions } from '../Options/StepOption'
import { getCredentialsByUseCase } from '@/services/credentialsService'
import { ActionMenu } from '@/components/custom/Table/TableActionMenu'
import { WorkflowStepType } from '@/constants/workflows/workflowCreate'
import { FieldMappingV2Modal } from '@/pages/Workflows/components/Modals/FieldMappingV2'

export const StepTask = ({
  wfStep,
  index,
}: {
  wfStep: WFCreateStep,
  index: number,
}) => {
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const { applications } = useSelector((state: RootState) => state.application)
  const { create } = useSelector((state: RootState) => state.workflow)
  const [actionAndSubject, setActionAndSubject] = useState<ActionsAndSubjectsResponse[]>([]);
  const [loginMethods, setLoginMethods] = useState<LoginMethod[]>([])
  const [createNewCredentialModalIsOpen, setCreateNewCredentialModalIsOpen] = useState(false)
  const [fieldMappingModal, showFieldMappingModal] = useState(false)
  const [appSelectModal, setAppSelectModal] = useState(false)
  const app = applications.find(ds => ds.id === wfStep.id)
  const actions = getAllActionsByApp(actionAndSubject)
  const subjects = getAllSubjectsByAppAndAction(actionAndSubject, wfStep.action)
  const fetchedSets = useRef<Set<string>>(new Set());
  const [selectableCredentials, setSelectableCredentials] = useState<{ id: string, value: string }[]>([]);

  const menuItems = [
    { label: "Change Step app", onClick: () => setAppSelectModal(true), disabled: false, icon: <SquarePen className="text-accent" size={16} /> },
    { label: "Remove Step", onClick: () => dispatch(workflowActions.removeStepByStepId(wfStep)), disabled: false, divider: true, icon: <Trash2 className="text-destructive" size={16} /> },
  ];

  // reset Subject if Action is changed
  useEffect(() => {
    if (wfStep?.subject?.length && wfStep.action?.length && actionAndSubject.length) {
      const hasSubject = actionAndSubject.find(a => a.actionCode === wfStep.action)?.availableSubjects.find(s => s.code === wfStep.subject)
      if (!hasSubject) {
        dispatch(workflowActions.changeStepSubject({
          id: wfStep.id,
          order: wfStep.order,
          subject: ''
        }))
      }
    }
  }, [wfStep.action, wfStep.subject, dispatch, actionAndSubject, wfStep.id, wfStep.order])

  useEffect(() => {
    if (token && wfStep.id) {
      const fetchAppActionAndSubject = async () => {
        const resp = await dispatch(getActionsAndSubjectsByApp(token, wfStep.id))
        if (resp.data) {
          setActionAndSubject(resp.data)
        }
      }
      fetchAppActionAndSubject()
        .catch(() => console.log('Error while getting Subjects: ', wfStep.id))
    }
  }, [token, wfStep.id, dispatch])

  // set Required Options to store
  useEffect(() => {
    if (wfStep.action?.length && wfStep.subject?.length) {
      const requiredOptions = actionAndSubject.find(a => a.actionCode === wfStep.action)
        ?.availableSubjects.find(s => s.code === wfStep.subject)
        ?.options?.filter(o => o.required)?.map(o => o.code)

      dispatch(workflowActions.updateStepRequiredOptions({
        id: wfStep.id,
        options: requiredOptions ?? []
      }))
    }
  }, [wfStep.action, wfStep.subject, wfStep.id, actionAndSubject, dispatch])

  useEffect(() => {
    if (
      wfStep.action.length &&
      wfStep.subject.length &&
      wfStep.credentialId.length &&
      wfStep.filters &&
      wfStep.options &&
      actionAndSubject
    ) {
      // check if all required filters are filled in
      const responsibleFilters = actionAndSubject?.find(a => a.actionCode === wfStep.action)
        ?.availableSubjects.find(s => s.code === wfStep.subject)
        ?.filters

      const responsibleOptions = actionAndSubject?.find(a => a.actionCode === wfStep.action)
        ?.availableSubjects.find(s => s.code === wfStep.subject)
        ?.options

      const areRequiredFiltersSatisfied = (
        responseFilters?: ActionSubjectFilter[],
      ) => {
        if (!responseFilters) return false
        const selectedMap = new Map(wfStep.filters.map(f => [f.filter, f.filterValue]));

        const isDependencyMet = (dependsOn?: FilterDependsOn[]) => {
          if (!dependsOn || dependsOn.length === 0) return true;
          return dependsOn.every(dep => {
            const selectedValue = dep.filterCode ? selectedMap.get(dep.filterCode) : undefined;
            if (!selectedValue) return false;
            if (dep.filterTrueValues?.length) {
              return dep.filterTrueValues.includes(selectedValue);
            }
            return true;
          });
        };
        for (const filter of responseFilters) {
          if (filter.required && isDependencyMet(filter.dependsOn)) {
            const selectedValue = selectedMap.get(filter.code);
            if (!selectedValue) return false;
          }
        }
        return true;
      }

      const areRequiredOptionsSatisfied = (
        responseOptions?: ActionSubjectOption[],
      ) => {
        if (!responseOptions) return false
        const selectedMap = new Map(wfStep.options.map(f => [f.option, f.optionValue]));

        const isDependencyMet = (dependsOn?: OptionDependsOn[]) => {
          if (!dependsOn || dependsOn.length === 0) return true;
          return dependsOn.every(dep => {
            const selectedValue = dep.optionCode ? selectedMap.get(dep.optionCode) : undefined;
            if (!selectedValue) return false;
            if (dep.optionTrueValues?.length) {
              return dep.optionTrueValues.includes(selectedValue);
            }
            return true;
          });
        };
        for (const option of responseOptions) {
          if (option.required && isDependencyMet(option.dependsOn)) {
            const selectedValue = selectedMap.get(option.code);
            if (!selectedValue) return false;
          }
        }
        return true;
      }

      // only fetch fields if all required filters are filled in
      if (areRequiredFiltersSatisfied(responsibleFilters) && areRequiredOptionsSatisfied(responsibleOptions)) {
        const payload = {
          action: wfStep.action,
          subject: wfStep.subject,
          credential: wfStep.credentialId,
          appId: wfStep.id,
          filters: wfStep.filters.map(sf => ({ filterCode: sf.filter, value: sf.filterValue })),
          options: wfStep.options.map(sf => ({ optionCode: sf.option, value: sf.optionValue })),
        };

        const payloadKey = JSON.stringify(payload);

        if (!fetchedSets.current.has(payloadKey)) {
          fetchedSets.current.add(payloadKey);
          fetchAppFields(payload).catch(() => console.log(`Error while getting Fields for Step ${index}`));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wfStep.action, wfStep.subject, wfStep.credentialId, wfStep.filters, wfStep.options, actionAndSubject, wfStep.id])

  const fetchAppFields = async (fieldToFetch: AppFieldsToFetch) => {
    if (!token) return
    const resp = await dispatch(getAppAvailableFields(
      token,
      fieldToFetch.action,
      fieldToFetch.subject,
      fieldToFetch.credential,
      fieldToFetch.appId,
      fieldToFetch.filters,
      fieldToFetch.options
    ))
    if (resp.data) {
      dispatch(workflowActions.setStepAvailableFields({
        stepId: wfStep.id,
        order: wfStep.order,
        fields: resp.data
      }))
    }
  }

  // TODO Remove after test in Sandbox

  // const getSelectedPreviousAppNames = (stepOrder: number) => {
  //   const stepsApps = create.steps
  //     .filter((step) => step.order < stepOrder)
  //     .map(t => {
  //       const app = applications.find(app => app.id === t.id);
  //       return app ? `${t.order};${app.name}` : null;
  //     })
  //     .filter(Boolean) as string[];

  //   return stepsApps;
  // };

  const getAllFields = (wfStepOrder: number) => {
    const result: Partial<Record<string, FieldsResponse>> = {};

    const filteredSteps = create.steps
      .filter(step => step.order <= wfStepOrder)
      .sort((a, b) => a.order - b.order);

    filteredSteps.forEach(step => {
      const app = applications.find(a => a.id === step.id);

      if (step.availableFields) {
        result[`${step.order};${app?.name || WorkflowStepType.TRANSFORMATION}`] = step.availableFields;
      }
    });

    return result;
  };

  // setSelectableCredentials & setLoginMethods
  useEffect(() => {
    if (token && wfStep.action?.length && wfStep.subject?.length && app?.id) {
      setLoginMethods([]);
      const fetchUseCaseCredentials = async () => {
        const resp = await dispatch(getCredentialsByUseCase(
          token,
          app.id,
          wfStep.action,
          wfStep.subject
        ))

        if (resp.data) {
          if (0 < resp.data.length) {
            setSelectableCredentials(resp.data.map(c => ({ id: c.id, value: c.nickname })));
          } else {
            const fetchCredentialSourceLoginMethods = async () => {
              const resp = await dispatch(getLoginMethodsOfApp(token, app.id))
              if (resp.data?.loginMethods) {
                setLoginMethods(resp.data.loginMethods)
              }
            }

            fetchCredentialSourceLoginMethods()
              .catch(() => console.log('Error while getting Login methods of a credential source: ', app.name))
          }
        }
      }

      fetchUseCaseCredentials()
        .catch(() => console.log('Error while getting credentials: ', wfStep.id))
    }
  }, [token, wfStep.action, wfStep.subject, wfStep.id, dispatch, app?.id, app?.name])

  return app && wfStep && (
    <>
      <Section
        key={wfStep.id + index}
        title={`Step ${index + 1}`}
        subtitle="Configure Step"
        description="Choose what you want to do with the data from this App"
      >
        <InputFieldWrapper title="App">
          <div className='mt-2' />
          <div className="w-full flex items-center justify-between">
            <AppName
              name={app.displayName}
              logo={app.logo}
            />
            <div className="-mt-5 text-primary">
              <ActionMenu items={menuItems} menuType='vertical' />
            </div>
          </div>
        </InputFieldWrapper>
        <div className="my-8" />
        <InputFieldWrapper title={`What are we doing with ${app?.displayName}?`}>
          <div className='mt-2' />
          <DestinationSelectInputField
            label='Action'
            value={wfStep?.action || ''}
            selectOptions={actions}
            onChange={value => dispatch(workflowActions.changeStepAction({
              id: wfStep.id,
              order: wfStep.order,
              action: value
            }))}
          />
          <div className='my-4' />
          <DestinationSelectInputField
            label='Subject'
            value={wfStep?.subject || ''}
            selectOptions={subjects}
            onChange={value => dispatch(workflowActions.changeStepSubject({
              id: wfStep.id,
              order: wfStep.order,
              subject: value
            }))}
            isDisabled={wfStep?.action?.length === 0}
          />
        </InputFieldWrapper>
        <div className='my-8' />
        <InputFieldWrapper title="Credentials">
          <div className='mt-2' />
          <DestinationSelectInputField
            value={wfStep?.credentialId || ''}
            selectOptions={selectableCredentials}
            onChange={value => dispatch(workflowActions.changeStepCredential({
              id: wfStep.id,
              order: wfStep.order,
              credentialId: value
            }))}
            isDisabled={wfStep?.action?.length === 0 || wfStep?.subject?.length === 0}
          />
          {0 < loginMethods.length && (
            <div className="flex flex-col items-start my-2">
              <div className="flex">
                <img src={errorIcon} className="mr-2" />
                <p className="text-destructive text-sm">Seems like you don't have any {app?.displayName} Credentials.</p>
              </div>
              <p className="text-destructive text-sm">
                Click {' '}
                <span
                  className="font-bold underline cursor-pointer"
                  onClick={() => setCreateNewCredentialModalIsOpen(true)}
                >
                  here{' '}
                </span>
                to create one
              </p>
            </div>
          )}
          {/* Modal */}
          {app && loginMethods && (
            <CreateAndUseNewCredential
              key={app.id}
              isOpen={createNewCredentialModalIsOpen}
              onClose={() => setCreateNewCredentialModalIsOpen(false)}
              credentialSource={app}
              loginMethods={loginMethods}
            />
          )}
        </InputFieldWrapper>
        <div className='my-8' />
        <StepFilters
          stepId={wfStep.id}
          action={wfStep.action}
          subject={wfStep.subject}
          credential={wfStep.credentialId}
          selectedFilters={wfStep.filters}
          filtersToFetch={actionAndSubject?.find(a => a.actionCode === wfStep?.action)
            ?.availableSubjects.find(s => s.code === wfStep?.subject)
            ?.filters
          }
          order={wfStep.order}
          app={app}
          availableFields={wfStep.availableFields}
        />
        <div className='my-8' />
        <StepOptions
          stepId={wfStep.id}
          action={wfStep.action}
          subject={wfStep.subject}
          credential={wfStep.credentialId}
          selectedOptions={wfStep.options}
          optionsToFetch={actionAndSubject?.find(a => a.actionCode === wfStep?.action)
            ?.availableSubjects.find(s => s.code === wfStep?.subject)
            ?.options
          }
          order={wfStep.order}
          app={app}
        />
        <div className='my-8' />
        {index > 0 ? (
          <InputFieldWrapper
            title="Field Mappings"
            subtitle={`Configure all the fields which you want to sync to ${app?.displayName}`}>
            <div className='my-4' />
            <div className="flex justify-start">
              <Button
                type='button'
                className="w-auto font-semibold"
                variant={ButtonVariant.DEFAULT}
                onClick={() => showFieldMappingModal(true)}
                disabled={!wfStep?.availableFields}
              >
                Manage Fields
              </Button>
              <FieldMappingV2Modal
                order={wfStep.order}
                showModal={fieldMappingModal}
                setShowModal={showFieldMappingModal}
                app={app}
                allFields={getAllFields(wfStep.order) as Record<string, FieldsResponse>}
                actionDisplayName={actions?.find(a => a.id === wfStep?.action)?.value ?? ''}
                subjectDisplayName={subjects?.find(s => s.id === wfStep?.subject)?.value ?? ''}
              />
            </div>
          </InputFieldWrapper>
        ) : null}
      </Section>
      <EditTaskApp
        order={wfStep.order}
        isSelectAppModalOpen={appSelectModal}
        setIsSelectModalOpen={setAppSelectModal}
        taskId={wfStep.id}
        applications={applications}
      />
    </>
  )
}
